import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import {
  GraduationCap,
  ArrowLeft,
  Send,
  Calendar,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { todayLocal } from '../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const TeacherReportForm = () => {
  const navigate = useNavigate();
  const { linkId } = useParams();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [reportDate, setReportDate] = useState(todayLocal());
  const [reportType, setReportType] = useState('daily');
  const [overallRating, setOverallRating] = useState('');
  const [behavioralRegulation, setBehavioralRegulation] = useState([]);
  const [behavioralSeverity, setBehavioralSeverity] = useState('');
  const [ocdBehaviors, setOcdBehaviors] = useState([]);
  const [attentionLearning, setAttentionLearning] = useState([]);
  const [physicalSigns, setPhysicalSigns] = useState([]);
  const [sensoryEnvironmental, setSensoryEnvironmental] = useState([]);
  const [sleepEnergy, setSleepEnergy] = useState([]);
  const [socialInteraction, setSocialInteraction] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [comment, setComment] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('teacher_token');
    if (!token) {
      navigate('/teacher/login');
      return;
    }
    fetchStudentDetails(token);
  }, [linkId, navigate]);

  const fetchStudentDetails = async (token) => {
    try {
      const response = await axios.get(`${API}/api/teacher/my-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const foundStudent = response.data.students.find(s => s.id === linkId);
      if (foundStudent) {
        setStudent(foundStudent);
      } else {
        toast.error('Student not found');
        navigate('/teacher/portal');
      }
    } catch (error) {
      console.error('Error fetching student:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCheckbox = (value, currentList, setList) => {
    if (currentList.includes(value)) {
      setList(currentList.filter(v => v !== value));
    } else {
      setList([...currentList, value]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!overallRating) {
      toast.error('Please select an overall day rating');
      return;
    }

    setSubmitting(true);
    const token = localStorage.getItem('teacher_token');

    try {
      await axios.post(`${API}/api/teacher/report`, {
        teacher_link_id: linkId,
        report_date: reportDate,
        report_type: reportType,
        overall_rating: overallRating,
        behavioral_regulation: behavioralRegulation,
        behavioral_severity: behavioralSeverity || null,
        ocd_behaviors: ocdBehaviors,
        attention_learning: attentionLearning,
        physical_signs: physicalSigns,
        sensory_environmental: sensoryEnvironmental,
        sleep_energy: sleepEnergy,
        social_interaction: socialInteraction,
        triggers: triggers,
        comment: comment || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Report submitted successfully!');
      navigate('/teacher/portal');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const CheckboxGroup = ({ title, options, selected, setSelected, columns = 2 }) => (
    <div className="space-y-3">
      <h3 className="font-semibold text-gray-900">{title}</h3>
      <div className={`grid grid-cols-1 sm:grid-cols-${columns} gap-2`}>
        {options.map(({ value, label }) => (
          <label
            key={value}
            className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
              selected.includes(value)
                ? 'bg-primary/10 border-primary'
                : 'bg-white border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="checkbox"
              checked={selected.includes(value)}
              onChange={() => toggleCheckbox(value, selected, setSelected)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const RadioGroup = ({ title, options, selected, setSelected, required = false }) => (
    <div className="space-y-3">
      <h3 className="font-semibold text-gray-900">
        {title}
        {required && <span className="text-red-500 ml-1">*</span>}
      </h3>
      <div className="space-y-2">
        {options.map(({ value, label, color }) => (
          <label
            key={value}
            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              selected === value
                ? `${color || 'bg-primary/10 border-primary'}`
                : 'bg-white border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name={title}
              checked={selected === value}
              onChange={() => setSelected(value)}
              className="text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700">{label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/teacher/portal')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-primary" />
            <span className="font-semibold text-gray-900">Daily Check-In</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        <Card className="p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <span className="text-xl font-bold text-primary">
                {student?.child_name?.charAt(0)}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{student?.child_name}</h2>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="w-4 h-4" />
                <input
                  type="date"
                  value={reportDate}
                  onChange={(e) => setReportDate(e.target.value)}
                  className="border-none p-0 focus:ring-0 text-gray-500"
                />
              </div>
            </div>
          </div>

          {/* Report Type */}
          <div className="flex gap-2 mb-6">
            <Button
              type="button"
              variant={reportType === 'daily' ? 'default' : 'outline'}
              onClick={() => setReportType('daily')}
              size="sm"
            >
              Daily Report
            </Button>
            <Button
              type="button"
              variant={reportType === 'weekly' ? 'default' : 'outline'}
              onClick={() => setReportType('weekly')}
              size="sm"
            >
              Weekly Summary
            </Button>
          </div>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 1. Overall Day Rating */}
          <Card className="p-6">
            <RadioGroup
              title="1️⃣ Overall Day Rating"
              required
              selected={overallRating}
              setSelected={setOverallRating}
              options={[
                { value: 'much_better', label: 'Much better than usual', color: 'bg-green-100 border-green-500' },
                { value: 'slightly_better', label: 'Slightly better', color: 'bg-green-50 border-green-400' },
                { value: 'typical', label: 'Typical baseline', color: 'bg-gray-100 border-gray-400' },
                { value: 'slightly_worse', label: 'Slightly worse', color: 'bg-orange-50 border-orange-400' },
                { value: 'much_worse', label: 'Much worse than usual', color: 'bg-red-100 border-red-500' }
              ]}
            />
          </Card>

          {/* 2. Behavioral Regulation */}
          <Card className="p-6">
            <CheckboxGroup
              title="2️⃣ Behavioral Regulation"
              selected={behavioralRegulation}
              setSelected={setBehavioralRegulation}
              options={[
                { value: 'irritability', label: 'Increased irritability' },
                { value: 'outbursts', label: 'Emotional outbursts' },
                { value: 'tearfulness', label: 'Tearfulness' },
                { value: 'anxiety', label: 'Anxiety / worry observed' },
                { value: 'rigid', label: 'Rigid / difficulty with transitions' },
                { value: 'aggression', label: 'Aggression (verbal or physical)' },
                { value: 'withdrawn', label: 'Withdrawn / shut down' },
                { value: 'typical', label: 'Typical regulation' }
              ]}
            />
            {behavioralRegulation.length > 0 && !behavioralRegulation.includes('typical') && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                <div className="flex gap-2">
                  {['mild', 'moderate', 'severe'].map(level => (
                    <Button
                      key={level}
                      type="button"
                      variant={behavioralSeverity === level ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setBehavioralSeverity(level)}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* 3. OCD / Compulsive Behaviors */}
          <Card className="p-6">
            <CheckboxGroup
              title="3️⃣ OCD / Compulsive Behaviors Observed"
              selected={ocdBehaviors}
              setSelected={setOcdBehaviors}
              options={[
                { value: 'repetitive_questioning', label: 'Repetitive questioning' },
                { value: 'reassurance_seeking', label: 'Reassurance seeking' },
                { value: 'ritualistic', label: 'Ritualistic behaviors' },
                { value: 'avoidance', label: 'Avoidance behaviors' },
                { value: 'none', label: 'None observed' }
              ]}
            />
          </Card>

          {/* 4. Attention & Learning */}
          <Card className="p-6">
            <CheckboxGroup
              title="4️⃣ Attention & Learning"
              selected={attentionLearning}
              setSelected={setAttentionLearning}
              options={[
                { value: 'difficulty_concentrating', label: 'Difficulty concentrating' },
                { value: 'slow_processing', label: 'Slow processing' },
                { value: 'confused', label: 'Appeared confused' },
                { value: 'extra_prompting', label: 'Needed extra prompting' },
                { value: 'regression', label: 'Significant academic regression' },
                { value: 'typical', label: 'Typical performance' }
              ]}
            />
          </Card>

          {/* 5. Physical / Neurological Signs */}
          <Card className="p-6">
            <CheckboxGroup
              title="5️⃣ Physical / Neurological Signs"
              selected={physicalSigns}
              setSelected={setPhysicalSigns}
              options={[
                { value: 'tics_motor', label: 'Tics (motor)' },
                { value: 'tics_vocal', label: 'Tics (vocal)' },
                { value: 'choreiform', label: 'Choreiform movements' },
                { value: 'bathroom', label: 'Frequent bathroom requests' },
                { value: 'fatigue', label: 'Fatigue' },
                { value: 'headache', label: 'Headache complaints' },
                { value: 'stomachache', label: 'Stomachache complaints' },
                { value: 'none', label: 'None observed' }
              ]}
            />
          </Card>

          {/* 6. Sensory & Environmental */}
          <Card className="p-6">
            <CheckboxGroup
              title="6️⃣ Sensory & Environmental Tolerance"
              selected={sensoryEnvironmental}
              setSelected={setSensoryEnvironmental}
              options={[
                { value: 'noise', label: 'Noise sensitivity' },
                { value: 'light', label: 'Light sensitivity' },
                { value: 'clothing', label: 'Clothing discomfort' },
                { value: 'overwhelmed_groups', label: 'Overwhelmed in group settings' },
                { value: 'quiet_space', label: 'Needed quiet space' }
              ]}
            />
          </Card>

          {/* 7. Sleep & Energy */}
          <Card className="p-6">
            <CheckboxGroup
              title="7️⃣ Sleep & Energy (Teacher Observation)"
              selected={sleepEnergy}
              setSelected={setSleepEnergy}
              options={[
                { value: 'tired', label: 'Appeared tired' },
                { value: 'fell_asleep', label: 'Fell asleep in class' },
                { value: 'hyperactive', label: 'Hyperactive/restless' },
                { value: 'typical', label: 'Typical energy' }
              ]}
            />
          </Card>

          {/* 8. Peer & Social */}
          <Card className="p-6">
            <CheckboxGroup
              title="8️⃣ Peer & Social Interaction"
              selected={socialInteraction}
              setSelected={setSocialInteraction}
              options={[
                { value: 'withdrawal', label: 'Social withdrawal' },
                { value: 'conflict', label: 'Conflict with peers' },
                { value: 'clingy', label: 'Clingy toward adults' },
                { value: 'typical', label: 'Age-appropriate interaction' }
              ]}
            />
          </Card>

          {/* 9. Triggers */}
          <Card className="p-6">
            <CheckboxGroup
              title="9️⃣ Notable Triggers or Events (Optional)"
              selected={triggers}
              setSelected={setTriggers}
              options={[
                { value: 'schedule_change', label: 'Schedule change' },
                { value: 'substitute', label: 'Substitute teacher' },
                { value: 'test', label: 'Test or academic pressure' },
                { value: 'peer_conflict', label: 'Conflict with peer' },
                { value: 'loud_event', label: 'Loud event / assembly' },
                { value: 'illness', label: 'Illness in classroom' },
                { value: 'none', label: 'None noted' }
              ]}
            />
          </Card>

          {/* 10. Comment */}
          <Card className="p-6">
            <h3 className="font-semibold text-gray-900 mb-3">
              🔟 Brief Comment (Optional)
            </h3>
            <p className="text-sm text-gray-500 mb-3">
              Anything important parents should know today?
            </p>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              rows={3}
              placeholder="e.g., Emma needed extra breaks today but did well in small group work..."
              data-testid="teacher-report-comment"
            />
          </Card>

          {/* Submit */}
          <div className="sticky bottom-0 bg-gradient-to-t from-blue-50 to-transparent pt-4 pb-6">
            <Button
              type="submit"
              className="w-full h-14 text-lg"
              disabled={submitting || !overallRating}
              data-testid="teacher-report-submit"
            >
              {submitting ? (
                'Submitting...'
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Submit Report
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default TeacherReportForm;
